import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  themeUtils as tu,
  Table,
  Title,
  Panel,
  Box,
  Pending,
  useModal,
  Alert,
  notification,
  Modal,
  ModalContent,
} from '@zen-common/components-base';
import { numberUtils } from '@zen/common-utils';
import { DexSearchStore } from '@zen-dex/utils';
import { useNavigation } from 'react-navi';
import { NamingAssetStore, WalletStore, NamingUtils } from '@zen/common-app-parts';
import * as priceUtils from '../../../../../utils/priceUtils';
import TakeButton from '../components/Take/TakeButton';
import usePreventAction from '../../../../../utils/usePreventAction';
import PATHS from '../../../../../paths';
import { getRedirectTo } from '../../../../../utils/redirectsUtils';
import { contentXPadding } from './modules/contentXPadding';
import TakeModalProcess from './Take/TakeModalProcess';

const OrderBookTable = ({ title, isBuy, isMobile }) => {
  const { state } = DexSearchStore.useStore();
  const { state: naming } = NamingAssetStore.useStore();
  //needed to handle mobile on row click
  const { show, hide } = useModal();
  const { preventAction } = usePreventAction();
  const navigation = useNavigation();
  const {
    state: { isLoggedIn, hasPassword },
  } = WalletStore.useStore();

  function handleCancel() {
    hide();
  }
  function handleError(error) {
    show(() => (
      <Alert type={Alert.TYPE.ERROR} onDismiss={hide} title="Error">
        {error.message}
      </Alert>
    ));
  }
  function handleSuccess() {
    notification('Order was taken successfully', {
      type: notification.TYPE.SUCCESS,
    });
    hide();
  }

  const openModal = ({ price, quantity, order }) => {
    if (!preventAction()) {
      show(() => (
        <Modal>
          <ModalContent>
            <TakeModalProcess
              order={order}
              base={state.base}
              quote={state.quote}
              isBuy={isBuy}
              price={price}
              quantity={quantity}
              onCancel={handleCancel}
              onSuccess={handleSuccess}
              onError={handleError}
            />
          </ModalContent>
        </Modal>
      ));
    } else if (isLoggedIn) {
      navigation.navigate(
        getRedirectTo({
          base: state.base,
          quote: state.quote,
          path: PATHS.ORDER_BOOK,
        })
      );
    } else {
      navigation.navigate(
        getRedirectTo({
          base: state.base,
          quote: state.quote,
          path: hasPassword && !isLoggedIn ? PATHS.UNLOCK : PATHS.ONBOARDING,
        })
      );
    }
  };

  const data = isBuy ? state.orders.buy : state.orders.sell;
  const columns = useMemo(
    () =>
      getTableColumns({
        isBuy,
        base: state.base,
        quote: state.quote,
        isMobile,
        naming,
      }),
    [isBuy, state.base, state.quote, isMobile, naming]
  );

  return (
    <StyledPanel>
      <TitleWrapper>
        <Title size="4" mb="0">
          {title}
        </Title>
      </TitleWrapper>
      <TableWrapper>
        <StyledTable
          resizable={false}
          sortable={false}
          minRows={0}
          pageSize={(data || []).length}
          showPagination={false}
          columns={columns}
          data={data}
          loading={state.searching}
          getTrProps={(state, rowInfo) => {
            return {
              onClick: () => {
                const { price, quantity, ...order } = rowInfo.original;
                return isMobile ? openModal({ price, quantity, order }) : null;
              },
            };
          }}
        />
      </TableWrapper>
    </StyledPanel>
  );
};
OrderBookTable.propTypes = {
  title: PropTypes.string,
  loading: PropTypes.bool,
};

export default OrderBookTable;

const StyledPanel = styled(Panel)`
  padding: 0;
  display: flex;
  flex-direction: column;
  ${tu.mq({
    height: ['auto', null, null, '100%'],
  })}
`;

const TitleWrapper = styled(Panel)`
  border-bottom: 1px solid ${tu.color('borderDark')};
  padding: ${tu.space('smd')};
  ${contentXPadding}
`;

const TableWrapper = styled(Box)`
  background-color: ${tu.color('bg1')};
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  position: relative;
`;
const StyledTable = styled(Table)`
  &.ReactTable {
    .rt-tr-group {
      flex: 0 0 auto !important;
    }
    .rt-td {
      ${contentXPadding}
    }
    .rt-thead {
      .rt-th {
        ${contentXPadding}
      }
    }
  }

  @media screen and (min-width: ${tu.breakpoints.lg}) {
    position: absolute !important;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
  }

  ${tu.mq({
    height: ['300px', null, null, 'auto'],
  })}
`;

const StyledPrice = styled(Box).attrs((p) => ({
  color: p.isBuy ? 'success' : 'danger',
}))``;

/* eslint-disable react/display-name,react/prop-types */
const getTableColumns = ({ isBuy, base, quote, isMobile, naming } = {}) => [
  {
    accessor: 'price',
    Cell: ({ value }) => <StyledPrice isBuy={isBuy}>{priceUtils.toDisplay(value, 5)}</StyledPrice>,
    Header: 'Price',
  },
  {
    accessor: 'quantity',
    minWidth: 180,
    Cell: ({ value }) => <span>{numberUtils.toDisplay(value, 2)}</span>,
    Header: `Quantity ${base ? ' - ' + NamingUtils.displayName(base, naming, isMobile) : ''}`,
  },
  {
    accessor: 'total',
    minWidth: 180,
    className: 'align-right',
    headerClassName: 'align-right',
    Cell: ({ value }) => <span>{numberUtils.toDisplay(value)}</span>,
    Header: `Total ${quote ? ' - ' + NamingUtils.displayName(quote, naming, isMobile) : ''}`,
  },
  {
    minWidth: isMobile ? 30 : 80,
    className: 'align-right',
    headerClassName: 'align-right',
    Cell: ({ original }) => {
      const { price, quantity, ...order } = original;
      return order.pending && !isMobile ? (
        <Pending />
      ) : isMobile ? null : (
        <TakeButton {...{ isBuy, base, quote, price, quantity, order }} />
      );
    },
  },
];
